'use client';

import React, { useRef, useEffect } from 'react';
import { ImageBj, Button } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useExtendedRouter, useExtendedSearchParams } from '@hooks/next-routing-wrappers';
import { useSwrHireGuaranteeCheckout } from '@hooks/useSwrApi/endpoints/hire/useSwrHireGuaranteeCheckout';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { sanitizeHtml } from '@utils/strings/sanitizeHtml';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { CheckIcon } from '@heroicons/react/16/solid';

interface GuaranteedHiringBannerProps {
  className?: string;
  isServicePage?: boolean;
}

export const GuaranteedHiringBanner = (props: GuaranteedHiringBannerProps) => {
  const { className = '', isServicePage = false } = props;
  const { t } = useTranslation('common');
  const { locale, router } = useExtendedRouter();
  const { searchParamsAsObject } = useExtendedSearchParams();
  const { headerInfoData } = useHeaderInfoData(locale);
  const { isAuth, redirectToLogin } = useAuthClient();
  const guaranteedHiringBannerRef = useRef<HTMLDivElement>(null);

  const { trigger, isMutating } = useSwrHireGuaranteeCheckout({
    locale: locale || 'ro',
    successCallback: (response: { checkoutSessionUrl?: string }) => {
      if (response?.checkoutSessionUrl) {
        // Use full page navigation for cross-origin Stripe checkout
        window.location.replace(response.checkoutSessionUrl);
      }
    },
  });

  const isActive = headerInfoData?.hireGuaranteeActive === true;

  // Scroll to banner if scrollTo query param is present
  useEffect(() => {
    const scrollTo = searchParamsAsObject?.scrollTo;
    if (scrollTo === 'hire-guarantee' && guaranteedHiringBannerRef.current) {
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        guaranteedHiringBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const newParams = new URLSearchParams(window.location.search);
        newParams.delete('scrollTo');
        const newRelativePathQuery = window.location.pathname + (newParams.toString() ? `?${newParams.toString()}` : '');

        // Use router.replace to update URL without refreshing or adding history entry
        router.replace(newRelativePathQuery, { scroll: false });
      }, 100);
    }
  }, [searchParamsAsObject, router]);

  const handleBuyClick = () => {
    if (isActive) return;

    // For unauthenticated users, redirect to login with callback to services page banner
    // We use query param instead of hash because hashes seems to be stripped after login
    if (!isAuth) {
      redirectToLogin('/company/offer?scrollTo=hire-guarantee');
    } else {
      // For authenticated users, trigger checkout directly
      void trigger();
    }
  };

  return (
    <>
      {isServicePage ? (
        <div ref={guaranteedHiringBannerRef} className="flex h-full flex-col justify-between">
          <div>
            <h3 className="mb-5 text-left font-bold">
              {t('hire.guarantee.title')}
            </h3>

            <div>
              <div className="mb-1 flex items-start gap-2">
                <CheckIcon className="size-5 min-w-5 text-secondary" />
                <span>{t('guaranteed.hiring.benefit.1')}</span>
              </div>
              <div className="mb-1 flex items-start gap-2">
                <CheckIcon className="size-5 min-w-5 text-secondary" />
                <span>{t('guaranteed.hiring.benefit.2')}</span>
              </div>
              <div className="mb-1 flex items-start gap-2">
                <CheckIcon className="size-5 min-w-5 text-secondary" />
                <span>{t('guaranteed.hiring.benefit.3')}</span>
              </div>
              <div className="mb-1 flex items-start gap-2">
                <CheckIcon className="size-5 min-w-5 text-secondary" />
                <span>{t('guaranteed.hiring.benefit.4')}</span>
              </div>
            </div>

          </div>

          <div>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="text-3xl font-bold md:text-4xl">
                {t('hire.guarantee.price.amount')}
              </span>
              <span className="text-sm text-ink-medium">
                {t('hire.guarantee.price.desc')}
              </span>
            </div>

            <Button
              styling="solid"
              color="secondary"
              rounding="full"
              size="sm"
              className="mt-3 min-w-32"
              isLoading={isMutating}
              onClick={handleBuyClick}
              disabled={isActive}
            >
              {isActive ? t('global.status.active') : t('hire.guarantee.cta')}
            </Button>
          </div>
        </div>
      ) : (
        <div
          className={`relative overflow-hidden rounded-lg border border-dashed border-primary bg-surface p-5 shadow-lg md:p-6 ${className}`}
        >
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center md:gap-6">
            {/* Left side: Image and content */}
            <div
              className="flex flex-1 flex-col items-center justify-center gap-4 sm:flex-row md:justify-start md:gap-6"
            >
              {/* Image */}
              <div className="relative shrink-0">
                <div className="relative aspect-square w-28 md:w-36">
                  <ImageBj
                    src="/images/premium/guaranteed-hiring.png"
                    alt="Guaranteed hiring"
                    fill
                    className="object-contain"
                    priority
                  />
                </div>
              </div>

              {/* Title and Benefits */}
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <h3 className="text-xl font-bold md:text-2xl">
                  {t('hire.guarantee.title')}
                </h3>
                <p
                  className="text-sm text-ink-medium"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      t('hire.guarantee.subtitle').replace(/\n/g, '<br>'),
                    ),
                  }}
                />
              </div>
            </div>

            {/* Right side: Price and CTA */}
            <div className="flex shrink-0 flex-col items-center gap-2 md:items-start">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold md:text-4xl">
                  {t('hire.guarantee.price.amount')}
                  {' '}
                  + TVA
                </span>
                <span className="text-sm text-ink-medium">
                  {t('hire.guarantee.price.period')}
                </span>
              </div>

              <Button
                styling="solid"
                color="secondary"
                rounding="full"
                className="min-w-32 !font-bold"
                isLoading={isMutating}
                onClick={handleBuyClick}
                disabled={isActive}
              >
                {isActive ? t('global.status.active') : t('hire.guarantee.cta')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
